/*
Author: Jude Fernando
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import Survey from '@/components/survey'
import {
    BackSkip,
    ProgressBreadcrumb,
} from '@/components/survey/CustomizedSurveyNodes'
import SaveButton from '@/components/survey/CustomizedSurveyNodes/SaveButton/SaveButton'
import { patientQuestionnaireDetailsSubmitFormat } from '@/components/survey/SurveyModel'
import { INode } from '@/components/survey/SurveyModel/types/nodeTypes'
import { constants } from '@/constants'
import store, { RootState } from '@/reducers/store'
import { useApplyFieldsForNewNodes } from '@/state/survey/formSlice'
import {
    useGetPatientDetails,
    useGetPatientSurvey,
    useGetQUestionAnswers,
    useGetQuestionnaireStatus,
    useSetCurrentNode,
    useSetCurrentPage,
    useSetPrevPage,
} from '@/state/survey/patientSuerveySlice'
import {
    BasicAnswerSave,
    useUpdateAnswersSubmit,
} from '@/state/survey/saveAnswerSlice'
import { decodeJWT, Session } from '@/utils'
import formData from '@/utils/Validate/formData'
import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const PatientSurvey: React.FC = memo(() => {
    //useSetSurveyState, setSurveyState
    const {
        currentPage,
        selectedNode,
        currentQuestion,
        questionnaireDetails,
        answersObj,
        patient,
        QuestionnaireStatus,
    } = useSelector((state: RootState) => state.patientSurvey)
    const { answersRequest } = useSelector(
        (state: RootState) => state.patientAnswer
    )
    const form = useSelector((state: RootState) => state.form)
    const setCurrentNode = useSetCurrentNode()
    const getQuestionnaire = useGetPatientSurvey()
    const updatePrevPage = useSetPrevPage()
    const updateCurrentPage = useSetCurrentPage()
    const applyNewFieldValues = useApplyFieldsForNewNodes()
    const updateAnswersDispatch = useUpdateAnswersSubmit()
    const getPatientDetails = useGetPatientDetails()
    const getQuestionnaireAnswers = useGetQUestionAnswers()
    const getQuestionnaireStatus = useGetQuestionnaireStatus()
    const [complete, setComplete] = useState<boolean>(false)
    const prevPageRef = useRef('')
    const navigate = useNavigate()
    const isInitialRunRetQuestionnaire = useRef(true)
    const isInitialRunPatient = useRef(true)
    const elementRef = useRef<HTMLDivElement>(null)
    const [height, setHeight] = useState<number | null>(null)
    const [isPatientComplete, setIsPatientComplete] = useState<boolean>(false)

    const getFirstPage = useCallback((data: INode): string => {
        // Find the node with name "section"
        const sectionNode = data.nodes?.find(
            (node: INode) =>
                node.type === constants.section || node.type === 'Section'
        ) // ToDo: backend should send Section for all section types

        // Return the first node within the "nodes" array of the section node
        return sectionNode?.nodes && sectionNode.nodes[0]?.name
            ? sectionNode.nodes[0].name
            : ''
    }, [])

    const getLastPage = useCallback((data: INode): string => {
        // Find the node with name "section"
        const sectionNodes = data.nodes?.filter(
            (node: INode) =>
                node.type === constants.section || node.type === 'Section'
        )

        const sectionNode = sectionNodes?.[sectionNodes.length - 1]

        // Return the last node within the "nodes" array of the section node
        if (sectionNode?.nodes && sectionNode.nodes.length > 0) {
            const lastNode = sectionNode.nodes[sectionNode.nodes.length - 1]
            return lastNode?.name ?? ''
        }

        return ''
    }, [])

    useEffect(() => {
        if (isInitialRunPatient.current) {
            const token = decodeJWT(Session.ticket())
            getPatientDetails(token.sub)
            isInitialRunPatient.current = false
        }
    }, [getPatientDetails])

    useEffect(() => {
        if (QuestionnaireStatus && QuestionnaireStatus.statusId > 2) {
            navigate(`/patient-status/`)
        }
    }, [QuestionnaireStatus])

    useEffect(() => {
        if (
            isInitialRunRetQuestionnaire.current &&
            patient.questionnaireResponseId
        ) {
            getQuestionnaireAnswers(patient.questionnaireResponseId)
            getQuestionnaire(patient.questionnaireId)
        }
        if (patient.waitingListRecordId && patient.questionnaireResponseId) {
            setIsPatientComplete(false)
            getQuestionnaireStatus(
                patient.waitingListRecordId,
                patient.questionnaireResponseId
            )
        } else if (
            Object.prototype.hasOwnProperty.call(
                patient,
                'questionnaireResponseId'
            ) &&
            !patient.questionnaireResponseId
        ) {
            setIsPatientComplete(true)
        }
    }, [patient])

    useEffect(() => {
        if (isInitialRunRetQuestionnaire.current && patient.id) {
            const token = decodeJWT(Session.ticket())
            getPatientDetails(token.sub)
            //getQuestionnaire()
            getQuestionnaire(patient.questionnaireId)
        }
    }, [])

    useEffect(() => {
        if (answersRequest) {
            setCurrentNode(currentPage, form)

            setTimeout(() => {
                // have tomaccess current state without watching the currentQuestion from useState. This is due to the dynamic components will not alow us to watch the current change only.
                const latestQuestion =
                    store.getState().patientSurvey.currentQuestion
                if (questionnaireDetails) {
                    const lastNode = getLastPage(questionnaireDetails)
                    if (lastNode === latestQuestion?.name) {
                        setComplete(true)
                    }
                }
                const nextPage =
                    latestQuestion?.nextNode || latestQuestion?.name || ''
                updateCurrentPage(nextPage)

                if (latestQuestion?.nextNode) {
                    updatePrevPage(latestQuestion.nextNode, latestQuestion.name)
                }
            }, 50) // or 50ms if needed
        }
    }, [answersRequest, questionnaireDetails])

    useEffect(() => {
        let page = ''
        if (!currentPage && questionnaireDetails && answersObj) {
            page = getFirstPage(questionnaireDetails)
            applyNewFieldValues(questionnaireDetails, answersObj.answers) // update form object with actual values
            updateCurrentPage(page)
        }

        if (questionnaireDetails && currentPage && form) {
            if (prevPageRef.current !== currentPage) {
                setCurrentNode(currentPage, form)
                formData.setUnChangedData(currentPage, form[currentPage].value) //save original values of the question for future use
                prevPageRef.current = currentPage // Update previous page reference
            }
        }
    }, [currentPage, questionnaireDetails, form, answersObj])

    const handleClick = () => {
        if (currentQuestion?.name) {
            const answers: BasicAnswerSave[] =
                patientQuestionnaireDetailsSubmitFormat.generateQuestionAnswerSubmitFormat(
                    form,
                    currentQuestion
                )
            updateAnswersDispatch(answers, patient.questionnaireResponseId)
        }

        // updateCurrentPage(currentQuestion?.nextNode ? currentQuestion?.nextNode : currentQuestion?.name ? currentQuestion?.name  : '' );
        // if(currentQuestion?.nextNode)
        //      updatePrevPage(currentQuestion?.nextNode, currentQuestion?.name);
    }
    const handleScroll = () => {
        if (elementRef.current) {
            const container = elementRef.current
            const lastContent = container.lastElementChild as HTMLElement // Get the last content
            if (lastContent) {
                // Get the distance of the last element from the top of the container
                const lastContentOffset = lastContent.offsetTop

                // Get the viewport height
                const viewportHeight = window.innerHeight

                // Calculate the target scroll position (30% from the top of the viewport)
                const targetScrollPosition =
                    lastContentOffset - viewportHeight * 0.3

                // Scroll the container to the calculated position
                container.scrollTo({
                    top: targetScrollPosition,
                    behavior: 'smooth', // Smooth scrolling
                })
            }
        }
    }

    //Complete and submit - use this button to handle final submission.
    return (
        <Fragment>
            {isPatientComplete ? (
                <div className="flex justify-center">
                    <div className="py-4 px-6 text-xl font-semibold text-gray-800">
                        No pending questionnaires at the moment.
                    </div>
                </div>
            ) : (
                <Fragment>
                    {selectedNode ? (
                        <ProgressBreadcrumb selectedNode={selectedNode} />
                    ) : null}
                    <div
                        ref={elementRef}
                        style={{
                            overflowY: 'auto', // Make the container scrollable
                            position: 'relative',
                            ...(height !== null && { height: `${height}px` }), // Apply height only when it's available
                        }}
                        className="patient-questions-wrapper"
                    >
                        {complete === false && (
                            <>
                                {currentQuestion && questionnaireDetails ? (
                                    <BackSkip
                                        setComplete={setComplete}
                                        getFirstPage={getFirstPage}
                                        getLastPage={getLastPage}
                                        nodes={questionnaireDetails}
                                        question={currentQuestion}
                                    />
                                ) : null}
                                {selectedNode && patient ? (
                                    <Survey
                                        handleScroll={handleScroll}
                                        height={height}
                                        setHeight={setHeight}
                                        disable={false}
                                        config={selectedNode}
                                    ></Survey>
                                ) : null}
                                {currentQuestion &&
                                currentQuestion.name !== 'Complete' ? (
                                    <div className="flex justify-center items-center">
                                        <SaveButton
                                            type={'primary'}
                                            handleClick={() => {
                                                handleClick()
                                            }}
                                            stateProp={'disabled'}
                                            icon={false}
                                            text={'Save and continue'}
                                        />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </Fragment>
            )}
        </Fragment>
    )
})

export default PatientSurvey
